import { Trans } from '@lingui/react'
import classNames from 'clsx'
import { bool, number, shape, string } from 'prop-types'
import { compose } from 'redux'

import {
  MP_AT_TYPE_CART_BOTTOM,
  MP_AT_TYPE_CART_TOP,
  TENANT_AT,
  TENANT_IT,
} from 'shared/consts'
import { AD_SERVER_METADATA_DATA_ATTR } from 'shared/consts/adServer'
import {
  ANALYTICS_ATTR_IMAGE,
  ANALYTICS_ATTR_TITLE,
  DEFAULT_WIDGETS,
  TYPE_CART_BOTTOM,
  TYPE_CART_TOP,
} from 'shared/consts/crosssellWidgets'
import { PAGE_TYPE_SEARCH_RESULTS } from 'shared/consts/page'
import { PRESCRIPTION_TYPE_NO_PRESCRIPTION } from 'shared/consts/productTemplates'
import {
  EXPERIMENT_VARIATION,
  NEW_CSW,
  TOP_DEALS_LABEL,
} from 'shared/experiments/consts'
import { getExperiment } from 'shared/experiments/utils/experiments'
import { isMarketplace } from 'shared/experiments/utils/featureFlags'
import { isRebrandingITActive } from 'shared/experiments/utils/rebranding'
import { isAdServerCrossSellExperimentEnabled } from 'shared/utils/adServerUtils'
import { isMobile } from 'shared/utils/deviceClassUtils'
import getProductUrl from 'shared/utils/getProductUrl'
import { isEmpty } from 'shared/utils/objectUtils'
import { getProductTemplate } from 'shared/utils/productTemplateUtils'
import Button from 'views/components/atoms/Button'
import ButtonAsLink from 'views/components/molecules/ButtonAsLink'
import ButtonImage from 'views/components/molecules/ButtonImage'
import ImageWithDiscountBadge from 'views/components/molecules/ImageWithDiscountBadge'
import StarRating from 'views/components/molecules/StarRating'
import CartForm from 'views/components/organisms/FilteredProductList/SearchProductListItem/CartForm'
import {
  collectLabelsProps,
  serpMobileExperimentEnabledAndV3,
} from 'views/components/organisms/FilteredProductList/SearchProductListItem/helpers'
import SearchProductLabels from 'views/components/organisms/FilteredProductList/SearchProductListItem/SearchProductLabels'
import ProductPrice from 'views/components/organisms/ProductPrice'
import { SELLER_MODAL_ID } from 'views/components/organisms/SellerModal/names'
import ProductSliderItemShippingPrice from 'views/components/organisms/SliderHorizontal/ProductSliderItem/ProductSliderItemShippingPrice'
import { RenderTopDealLabel } from 'views/components/organisms/SliderHorizontal/ProductSliderItemLabel'
import Experiment from 'views/components/utils/optimizely/Experiment'
import { getSellerModalHook } from 'views/containers/browser/SellerModalContainer/clientsideHook'
import AlternativeProductSliderItem from 'views/containers/crosssell/CrosssellSliderCompact/ProductSliderItemCompact/index'
import withMountPoints from 'views/enhancers/withMountPoints'
import withShopAndRuntimeConfig from 'views/enhancers/withShopAndRuntimeConfig'
import { getDiscountToShow } from 'views/utils/discount'
import {
  mountPointsType,
  ProductType,
  publicRuntimeConfigType,
} from 'views/utils/propTypes'
import { adServerMetadata } from 'views/utils/propTypes/adserver'
import { sizesPropType } from 'views/utils/propTypes/img'

import { getAmount, resolveLazyLoad, shouldRenderTopDealLabel } from './helpers'

import './ProductSliderItem.scss'

const ProductSliderItem = props => {
  const {
    publicRuntimeConfig: {
      publicConfig: { deviceClass, tenant },
      pageProperties: { experiments, pageType },
    },
    publicRuntimeConfig,
    mountPoints,
    index,
    lazyLoad,
    noBorder,
    noSlider,
    product,
    redirectToCart,
    metadata,
    showBuyButton,
    goToProductButton,
    srcHeight,
    srcWidth,
    imageMinWidth,
    imageMaxWidth,
    imageSizes,
    thumbnail,
    whiteImageBg,
    wid,
    queryId,
    enableSponsoredLabel,
  } = props
  const {
    id,
    best_offer: bestOffer,
    canonicalURL,
    thumbnailURL,
    imageUrl,
    name,
    discount,
    rating,
    numberOfRatings,
    darreichung: pharmaForm,
    prices,
    unitPricing,
    offerId,
    offersCount,
    prescriptionType,
    sellerName,
    sellerId,
    shippingPrice,
    stockStatus,
    metadataId,
  } = product

  const newCswDesignExperiment = getExperiment(NEW_CSW, experiments)
  if (
    newCswDesignExperiment.isEnabled &&
    [EXPERIMENT_VARIATION.V1, EXPERIMENT_VARIATION.V2].includes(
      newCswDesignExperiment.variant
    )
  ) {
    return <AlternativeProductSliderItem {...props} />
  }

  const topDealExperiment = getExperiment(TOP_DEALS_LABEL, experiments)
  const shouldHaveTopDealLabel = shouldRenderTopDealLabel(topDealExperiment, id)
  const className = classNames('o-ProductSliderItem u-text-align--center', {
    'o-ProductSliderItem--white-img-bg': whiteImageBg,
    'u-padding--top': topDealExperiment.isEnabled && tenant !== TENANT_IT,
    'o-ProductSliderItem__isTopDeal':
      shouldHaveTopDealLabel ||
      (tenant === TENANT_IT && topDealExperiment.isEnabled),
  })
  const amount = getAmount(product)
  const discountsToShow = getDiscountToShow({ tenant, discount })
  const productMetadataContent = metadata[metadataId] || {}
  const trackingUrls = productMetadataContent.trackingUrls || {}
  const productUrl = trackingUrls.clickUrl || getProductUrl(canonicalURL)
  const sellerIdSAE = 'shop-apotheke'
  const showSellerBox =
    sellerId &&
    showBuyButton &&
    isMarketplace(experiments, tenant) &&
    sellerId !== sellerIdSAE &&
    offersCount &&
    offersCount === 1
  const shouldDisplayAbPrice =
    !showBuyButton &&
    offersCount &&
    offersCount > 1 &&
    isMarketplace(experiments, tenant)

  const shouldHaveSellerBoxClass =
    wid === DEFAULT_WIDGETS[tenant][TYPE_CART_BOTTOM] ||
    wid === DEFAULT_WIDGETS[tenant][TYPE_CART_TOP] ||
    wid === MP_AT_TYPE_CART_TOP ||
    wid === MP_AT_TYPE_CART_BOTTOM

  const shouldIncludeProductMetadataContentAsDataAttribute =
    isAdServerCrossSellExperimentEnabled(experiments) &&
    !isEmpty(productMetadataContent)

  const sliderItemProps = {
    className,
    'data-product-id': id,
    [AD_SERVER_METADATA_DATA_ATTR]:
      shouldIncludeProductMetadataContentAsDataAttribute
        ? JSON.stringify(productMetadataContent)
        : undefined,
  }
  const productTemplate = getProductTemplate({ ...product, tenant })

  const isRebranding = isRebrandingITActive(experiments)

  const runSerpMobileExperimentEnabledInV3 = serpMobileExperimentEnabledAndV3(
    experiments,
    pageType === PAGE_TYPE_SEARCH_RESULTS,
    isMobile(deviceClass)
  )

  const CTA = runSerpMobileExperimentEnabledInV3 ? (
    <div className="o-SearchProductListItem__content__buy-section__cart-section u-padding-small--sides">
      <CartForm
        algoliaQueryId={queryId}
        offerId={bestOffer?.id}
        position={index}
        prescriptionType={prescriptionType}
        productId={id}
        productTemplate={productTemplate}
        stockStatus={stockStatus}
        trackingUrls={trackingUrls}
      />
    </div>
  ) : (
    <div className="u-margin-small--top o-ProductSliderItem__add-to-cart__wrapper">
      <Button
        href={mountPoints.getAddToCart({
          cartAction: 'add',
          eventName: 'addToCart_crossSell',
          eventType: 'conversion',
          offer: offerId,
          prescriptionType: PRESCRIPTION_TYPE_NO_PRESCRIPTION,
          product: id,
          quantity: 1,
          redirectToCart,
        })}
        name={`product-slider-${id}-add-to-cart-button`}
        style="buttonGhost"
        className="o-ProductSliderItem__add-to-cart"
      >
        <Trans id="general.button.label.addToCart" />
      </Button>
    </div>
  )

  return (
    <article {...sliderItemProps}>
      {runSerpMobileExperimentEnabledInV3 && (
        <div className="o-SearchProductListItem__labels-CRO-2020-v3 u-position--relative">
          <SearchProductLabels
            {...collectLabelsProps(
              product,
              enableSponsoredLabel,
              publicRuntimeConfig,
              metadata
            )}
            mobileLabelsInitialNumber={2}
          />
        </div>
      )}
      <Experiment name={TOP_DEALS_LABEL}>
        {({ variant }) => {
          return shouldHaveTopDealLabel &&
            !runSerpMobileExperimentEnabledInV3 ? (
            <RenderTopDealLabel
              variant={variant}
              tenant={tenant}
              className="o-ProductSliderItem__label-item"
              firstFound={false}
            />
          ) : null
        }}
      </Experiment>
      <div
        className={classNames('o-ProductSliderItem__main u-padding--bottom', {
          'o-ProductSliderItem__main--no-border': noBorder,
          'o-ProductSliderItem__main--with-seller-box':
            shouldHaveSellerBoxClass || showBuyButton,
        })}
      >
        {!trackingUrls.impressionUrl ? null : (
          <small className="o-ProductSliderItem__sponsoredBadge">
            <Trans id="general.label.sponsored" />
          </small>
        )}
        <header className="o-ProductSliderItem__header u-position--relative u-margin-x-small--bottom">
          <div
            className="o-ProductSliderItem__image u-margin-large--bottom o-ProductSliderItem__header__item"
            data-clientside-hook="slider__image"
          >
            <ButtonImage
              name={`product-slider-${id}-image-button`}
              href={productUrl}
              noBorder
              className="u-position--relative"
              data-clientside-hook="productDetailButton"
            >
              <ImageWithDiscountBadge
                imageUrl={thumbnailURL || imageUrl}
                srcWidth={srcWidth}
                srcHeight={srcHeight}
                alt={name}
                discountPercent={discountsToShow}
                thumbnail={thumbnail}
                lazyLoad={resolveLazyLoad(deviceClass, index, lazyLoad)}
                imageAttrs={{
                  'data-analytics': ANALYTICS_ATTR_IMAGE,
                }}
                sizes={imageSizes}
                minWidth={imageMinWidth}
                maxWidth={imageMaxWidth}
              />
            </ButtonImage>
          </div>
          <h3
            className="o-ProductSliderItem__header__title a-p u-margin-x-small--bottom o-ProductSliderItem__header__item"
            data-clientside-hook="stretchVertically"
            data-stretch-group="name"
          >
            <ButtonAsLink
              href={productUrl}
              name={`product-slider-${id}-link-button`}
              className="o-ProductSliderItem__link"
              data-analytics={ANALYTICS_ATTR_TITLE}
              title={name}
              data-clientside-hook="productDetailButton"
            >
              {name}
            </ButtonAsLink>
          </h3>
          <StarRating
            rating={rating}
            ratingCount={numberOfRatings}
            centered
            invisible={!(rating > 0 || numberOfRatings > 0)}
            href={mountPoints.getProductReviews(productUrl)}
            hasSuperScript
          />
        </header>
        <ul className="o-ProductSliderItem__price-info a-list-reset">
          <li
            data-clientside-hook="stretchVertically"
            data-stretch-group="amount"
            className="o-ProductSliderItem__price-info__amount"
          >
            {amount}
            {Boolean(amount && pharmaForm) && ' | '}
            {pharmaForm}
          </li>
          <li
            data-clientside-hook="stretchVertically"
            data-stretch-group="product-price"
          >
            <ProductPrice
              prices={prices}
              size="x-small"
              displayInfo={false}
              showSuperScript={
                (showBuyButton && tenant === TENANT_AT) || tenant !== TENANT_AT
              }
              unitPricing={unitPricing}
              centered
              shouldDisplayAbPrice={shouldDisplayAbPrice}
            />
          </li>
        </ul>
        {showBuyButton && noSlider && isRebranding && CTA}
      </div>
      {(!noSlider || !isRebranding) && showBuyButton && CTA}
      {goToProductButton && (
        <div className="u-margin-small--top">
          <Button
            href={getProductUrl(canonicalURL)}
            name={`product-highlights-cta-${product.id}`}
            style="buttonGhost"
          >
            <Trans id="general.button.label.productLink" />
          </Button>
        </div>
      )}

      {Boolean(shippingPrice) && (
        <ProductSliderItemShippingPrice
          wid={wid}
          shippingPrice={shippingPrice}
        />
      )}

      {showSellerBox && (
        <div className="a-box u-padding-small u-text-align--center@small-tiny">
          <div className="a-p8 cross-sell-widget-text u-text-align--center@medium">
            <Trans id="message.soldAndShipped.partnerCrossSell" />
            <ButtonAsLink
              data-a11y-dialog-show={SELLER_MODAL_ID}
              href={`#${SELLER_MODAL_ID}`}
              data-clientside-hook={getSellerModalHook(sellerId)}
              className="partner-link-text u-padding-xx-small--left"
              data-qa-id="seller-name"
              data-target="seller-box"
            >
              {sellerName}
            </ButtonAsLink>
          </div>
        </div>
      )}
    </article>
  )
}

ProductSliderItem.propTypes = {
  publicRuntimeConfig: shape(publicRuntimeConfigType).isRequired,
  mountPoints: mountPointsType.isRequired,
  index: number,
  lazyLoad: bool,
  noBorder: bool,
  product: shape(ProductType).isRequired,
  metadata: adServerMetadata,
  showBuyButton: bool,
  goToProductButton: bool,
  noSlider: bool,
  redirectToCart: bool,
  srcHeight: number,
  srcWidth: number,
  imageMinWidth: number,
  imageMaxWidth: number,
  imageSizes: sizesPropType,
  thumbnail: bool,
  whiteImageBg: bool,
  wid: number,
  queryId: string,
  enableSponsoredLabel: bool,
}

ProductSliderItem.defaultProps = {
  enableSponsoredLabel: false,
  index: -1,
  lazyLoad: null,
  noBorder: false,
  metadata: {},
  showBuyButton: false,
  goToProductButton: false,
  noSlider: false,
  redirectToCart: false,
  srcHeight: 250,
  srcWidth: 250,
  imageMinWidth: null,
  imageMaxWidth: null,
  imageSizes: null,
  thumbnail: false,
  whiteImageBg: false,
  wid: -1,
  queryId: null,
}

export default compose(
  withMountPoints,
  withShopAndRuntimeConfig
)(ProductSliderItem)
